import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import "./invitation.scss";
// import CreateNewButton from "../../common/CreateNewButton";
import AddIcon from "../../../Assets/Icons/AddIcon.svg";
import { getMerchantInvites } from "../../../services/onboardingService";
import { useDispatch, useSelector } from "react-redux";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import Loader from "../../common/Loader";
import TableEmptyState from "../../common/TableEmptyState";

const InvitationListDataTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const [allInvites, setAllInvites] = useState([]);
  const [fullInvitesList, setFullInvitesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setAllInvites(data.slice(startIndex, endIndex));
  };

  const getInvitesData = async () => {
    try {
      const invitesPromise = await dispatch(
        getMerchantInvites({ limit: 1000 }) // Fetch more for client-side filtering
      )?.unwrap();
      if (invitesPromise?.success === true) {
        // Filter to show only merchants who are in the invitation flow.
        // We show those with an explicit invitation marker (notifiedAt) 
        // or those in an early status (pending, accepted, etc.) while excluding fully processed accounts.
        const requestedData = invitesPromise?.data || [];
        const invitedMerchants = (requestedData || []).filter(
          (invite) => {
            // We shouldn't strictly require a code if it's an admin invite.
            // Just ensure it has some identifying information like email or code.
            if (!invite.email && !invite.code && !invite?.user?.email) return false;

            // Check if they have already started the onboarding process
            // If they have a user account or any business details, they should be in the Merchant List
            const status = invite?.status?.toLowerCase();
            const hasAccount = !!invite.user || !!invite.userId;
            const bName = invite.businessName || invite.business_name || "";
            const hasBusinessInfo = !!bName && typeof bName === "string" && !bName.startsWith("Pending_");
            const hasStarted = hasAccount || hasBusinessInfo;

            // Exclude those who have started or are already in a processed status
            const isProcessed = ["completed", "accepted", "approved", "in-process", "in-progress"].includes(status) || hasStarted;

            if (isProcessed) return false;

            return true;
          }
        );

        // Sort by date descending (Latest on top)
        invitedMerchants.sort((a, b) => {
          const dateA = new Date(a.notifiedAt || a.createdAt || a.created_at || 0);
          const dateB = new Date(b.notifiedAt || b.createdAt || b.created_at || 0);
          return dateB - dateA;
        });

        setFullInvitesList(invitedMerchants);

        // Simple client-side pagination
        const total = Math.ceil((invitedMerchants?.length || 0) / 10);
        setTotalPages(total || 1);
        setCount(invitedMerchants?.length || 0);

        updatePageSlice(invitedMerchants, currentPage);
      }
    } catch (error) {
      console.error("Error in getInvitesData:", error);
    }
  };

  useEffect(() => {
    getInvitesData();
  }, []); // Initial load only

  useEffect(() => {
    if (fullInvitesList.length > 0) {
      updatePageSlice(fullInvitesList, currentPage);
    }
  }, [currentPage]);

  const items = [
    {
      title: "Merchants",
    },
    {
      title: "Invitation List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Date",
    },
    {
      name: "Email",
    },
    {
      name: "Code",
    },
    {
      name: "Status",
    },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of list when changing pages
    const container = document.getElementById('invitation-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {loading === true && <Loader />}
      <div className="flex flex-col sm:flex-row justify-between col-span-12 gap-4">
        <BreadCrumsHeader heading="Invitation List" items={items} />
        <div className="flex gap-3 items-center w-full sm:w-auto">


          <button
            onClick={() => navigate("/invitation-form")}
            className="relative w-full sm:w-[150px] lg:w-[170px] h-[38px] rounded-[5px] bg-[#00ADEF] text-[12px] lg:text-[14px] font-[600] font-nunito text-[#FFFFFF] text-center pl-[1.75rem] pr-[0.5rem] whitespace-nowrap flex items-center justify-center"
          >
            <img src={AddIcon} className="absolute left-[0.75rem] top-1/2 transform -translate-y-1/2 w-4 h-4" alt="Add" />
            <span className="ml-1">Invite New Merchant</span>
          </button>
        </div>
      </div>

      <div id="invitation-list-container" className="col-span-12 mt-8">
        {/* Desktop Table */}
        <TableContainer className="w-[100%] hidden md:block">
          <Table
            sx={{ borderCollapse: "separate", borderSpacing: "0px 8px" }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column, index) => {
                  return <TableCell key={index} className="!text-[#FFFFFF]">{column.name}</TableCell>;
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allInvites && allInvites.length > 0 &&
                allInvites.map((invite, index) => {
                  return (
                    <TableRow
                      key={invite?._id || index}
                      className="h-[50px]"
                      sx={{
                        boxShadow: "0px 4px 4px 0px #00000040",
                        backgroundColor:
                          (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                      }}
                    >
                      <TableCell>{(currentPage - 1) * 10 + index + 1}</TableCell>
                      <TableCell>
                        {
                          separateDateTimeAndConvertToLocale(invite?.createdAt || invite?.notifiedAt)
                            ?.localDate
                        }
                      </TableCell>
                      <TableCell>{invite?.email || invite?.user?.email || "N/A"}</TableCell>
                      <TableCell>{invite?.code || "N/A"}</TableCell>
                      <TableCell>
                        {(() => {
                          const status = invite?.status?.toLowerCase();
                          if (status === "pending" || !status) {
                            return (
                              <span className="px-3 py-1 rounded-full bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD] text-[12px] font-[500] whitespace-nowrap">
                                Invited
                              </span>
                            );
                          }
                          const isAccepted = status === "completed" || status === "accepted" || status === "approved" ||
                            status === "in-process" || status === "in-progress" || status === "pending";
                          if (isAccepted) {
                            return (
                              <span className="px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0] text-[12px] font-[500] whitespace-nowrap">
                                Accepted
                              </span>
                            );
                          }
                          return (
                            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-[12px] font-[500] whitespace-nowrap">
                              {invite?.status}
                            </span>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && allInvites && allInvites.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    className="!border-b-0 !p-0"
                  >
                    <TableEmptyState itemName="Invitation" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile Card View */}
        <div className="block md:hidden space-y-4">
          {allInvites && allInvites.length > 0 &&
            allInvites.map((invite, index) => (
              <div
                key={invite?._id || index}
                className="bg-white rounded-[10px] p-4 border border-[#E0E0E0] shadow-sm flex flex-col gap-3"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-[400] text-[#8C8C8C] font-poppins">Email</p>
                    <h3 className="text-[14px] font-[600] text-[#303030] font-poppins truncate">
                      {invite?.email || invite?.user?.email || "N/A"}
                    </h3>
                  </div>
                  {(() => {
                    const status = invite?.status?.toLowerCase();
                    if (status === "pending" || !status) {
                      return (
                        <span className="px-2 py-0.5 rounded-full bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD] text-[11px] font-[500] whitespace-nowrap">
                          Invited
                        </span>
                      );
                    }
                    const isAccepted = status === "completed" || status === "accepted" || status === "approved" ||
                      status === "in-process" || status === "in-progress" || status === "pending";
                    if (isAccepted) {
                      return (
                        <span className="px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0] text-[11px] font-[500] whitespace-nowrap">
                          Accepted
                        </span>
                      );
                    }
                    return (
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-[11px] font-[500] whitespace-nowrap">
                        {invite?.status}
                      </span>
                    );
                  })()}
                </div>

                <div className="grid grid-cols-2 gap-3 text-[13px] font-poppins text-[#303030]">
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">Date</span>
                    <span className="truncate">
                      {separateDateTimeAndConvertToLocale(invite?.createdAt || invite?.notifiedAt)?.localDate || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">Code</span>
                    <span className="truncate font-[600] text-[#00ADEF]">
                      {invite?.code || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          }
          {!loading && (!allInvites || allInvites.length === 0) && (
            <TableEmptyState itemName="Invitation" />
          )}
        </div>

        {allInvites && allInvites.length > 0 && (
          <Pagination
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
};

export default InvitationListDataTable;
